import { redeemHandoffCode } from '@/modules/api';
import { getLaunchParam } from '@/modules/clientContext';

/**
 * Entry-side redemption of the native-WebView auth handoff.
 *
 * Native apps open Sparta (www.vida.com/clients/onboarding) with a legacy JWT;
 * when the migration flag is on, Sparta brokers that session through Apigee's
 * /v1/auth/session-exchange and redirects the same WebView here with a
 * one-time `?handoff=<code>` (plus vida_client / vida_client_version). This
 * util redeems the code for a Bearer pair so the resuming user lands
 * authenticated instead of at account creation.
 *
 * Returns true when tokens were stored. The `handoff` param is stripped from
 * the URL either way — the code is opaque, short-TTL, and single-use, so it
 * has no value after the first redemption attempt (and it must never leak
 * into analytics page URLs or be re-sent by a reload). It is intentionally
 * NOT in PERSISTENT_LAUNCH_PARAMS, so internal navigation won't re-append it.
 *
 * The strip happens BEFORE awaiting redemption: `trackScreen` captures
 * `location.href` into the `$pageview` `$current_url`, so a pageview firing
 * during the in-flight redeem would otherwise leak the code into analytics.
 * `getLaunchParam` is memoized at module load, so the value survives the strip.
 *
 * No-op (false) when the param is absent — every non-native entry path.
 */
export async function redeemNativeHandoff(): Promise<boolean> {
  const handoff = getLaunchParam('handoff');
  if (!handoff) return false;

  stripHandoffFromUrl();
  return redeemHandoffCode(handoff);
}

function stripHandoffFromUrl(): void {
  if (
    typeof window === 'undefined' ||
    !window.location?.href ||
    typeof window.history?.replaceState !== 'function'
  ) {
    return;
  }
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('handoff')) return;
    url.searchParams.delete('handoff');
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // Best effort — a malformed URL should never break bootstrap.
  }
}
